// CharacterBadge.jsx
// Renders a flash-cast character. If the characterKey matches an existing
// flash design, render that PNG. Otherwise fall back to a styled SVG initial
// badge so scenes that reference characters not yet illustrated (Jacques,
// Poffridge, Nina) still render.
//
// When Wella delivers new character PNGs, they'll just appear automatically
// once they're added to config/flashDesigns.
import useFlashDesigns from "./../../hooks/useFlashDesigns";

// Display name overrides for character keys that aren't in the flash design
// catalog yet. Used by the SVG fallback for the initial + label.
const CHARACTER_NAMES = {
  jacques:    "Jacques",
  poffridge:  "Poffridge",
  nina:       "Nina",
  hermie:     "Hermie",
  charli:     "Charli",
  robert:     "Trav",
  saturnina:  "Saturnina",
  sunnynsugar:"Sunny & Sugar",
};

const FALLBACK_COLORS = [
  "var(--jop-blue)",
  "var(--jop-pink)",
  "var(--jop-orange)",
  "var(--jop-gold)",
  "var(--jop-highlight-blue)",
];

function colorForKey(key) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length];
}

/**
 * @param {string} characterKey - flash design id or fallback name
 * @param {number} size - pixel size (square)
 * @param {string} illustrationUrl - optional override (used by alerts that
 *   ship a custom character image)
 */
export default function CharacterBadge({ characterKey, size = 80, illustrationUrl, style = {} }) {
  const { designMap } = useFlashDesigns();
  const key = (characterKey || "").toLowerCase();
  const design = designMap[key] || designMap[characterKey];
  const src = illustrationUrl || (design && design.imageUrl);

  if (src) {
    return (
      <img
        src={src}
        alt={design?.label || characterKey || "character"}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }

  // SVG fallback — circular badge with the character's first initial
  const name = CHARACTER_NAMES[key] || characterKey || "?";
  const initial = name.charAt(0).toUpperCase();
  const color = colorForKey(key || "default");

  return (
    <div
      title={name}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        background: color,
        border: "3px solid var(--jop-cream)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "white",
        fontWeight: 900,
        fontSize: `${Math.round(size * 0.5)}px`,
        textShadow: "0 2px 4px rgba(0,0,0,0.4)",
        fontFamily: "system-ui, sans-serif",
        ...style,
      }}
    >
      {initial}
    </div>
  );
}
